import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Link } from '../../components/common/NextAdapters';
import toast from 'react-hot-toast';
import {
  useAdminCatalog, useSetMovieContentType, useSetMovieComingSoon, useSetMovieKidsContent,
  useMovieCast, useAdminActors, useAddCastMember, useRemoveCastMember,
} from '../../hooks/useMovie';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { useAlert } from '../../components/ui/AlertModal';
import EditMovieModal from './EditMovieModal';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const APPROVAL_STYLES = {
  Approved: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  Pending: 'bg-[#f0a818]/10 text-[#f0a818] ring-[#f0a818]/30',
  Rejected: 'bg-destructive/10 text-destructive ring-destructive/25',
};

function StatusBadge({ status }) {
  const style = APPROVAL_STYLES[status] || 'bg-white/5 text-muted-foreground ring-white/10';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${style}`}>
      {status}
    </span>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CastManager({ movieId }) {
  const { data: cast = [], isLoading } = useMovieCast(movieId);
  const { data: actors = [] } = useAdminActors();
  const addCast = useAddCastMember();
  const removeCast = useRemoveCastMember();
  const { confirm } = useAlert();

  const [actorId, setActorId] = useState('');
  const [characterName, setCharacterName] = useState('');

  const availableActors = actors.filter((a) => !cast.some((c) => c.Id === a.Id));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!actorId) return;
    const actor = availableActors.find((a) => String(a.Id) === actorId);
    const ok = await confirm(
      `Add ${actor?.Name || 'this actor'}${characterName ? ` as "${characterName}"` : ''} to the cast?`,
      { title: 'Add Cast Member', isConfirm: true, confirmLabel: 'Add' }
    );
    if (!ok) return;
    try {
      await addCast.mutateAsync({ movieId, actorId: parseInt(actorId, 10), characterName: characterName || null, sortOrder: cast.length });
      toast.success('Cast member added.');
      setActorId('');
      setCharacterName('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to add cast member.'));
    }
  };

  const handleRemove = async (aId, name) => {
    const ok = await confirm(`Remove ${name} from the cast of this title?`, {
      title: 'Remove Cast Member', variant: 'warning', isConfirm: true, confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await removeCast.mutateAsync({ movieId, actorId: aId });
      toast.success('Removed from cast.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to remove.'));
    }
  };

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading cast…</p>
      ) : cast.length === 0 ? (
        <p className="mb-3 text-xs text-muted-foreground">No cast credited yet.</p>
      ) : (
        <div className="mb-3 flex flex-wrap gap-2">
          {cast.map((c) => (
            <span
              key={c.Id}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] py-1 pl-3 pr-1.5 text-xs text-foreground"
            >
              {c.Name}{c.CharacterName ? ` as ${c.CharacterName}` : ''}
              <button
                type="button"
                onClick={() => handleRemove(c.Id, c.Name)}
                aria-label={`Remove ${c.Name}`}
                className="flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
              >
                <Icon icon="ph:x-bold" className="size-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex flex-col gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Actor</label>
          <SearchableSelect
            value={actorId}
            onValueChange={setActorId}
            options={availableActors.map((a) => ({ value: String(a.Id), label: a.Name }))}
            placeholder="Select actor…"
            searchPlaceholder="Search actors…"
            emptyText="No actors found."
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Character <span className="font-normal text-muted-foreground/60">(optional)</span>
          </label>
          <input
            className={inputClass}
            placeholder="Character name"
            value={characterName}
            onChange={(e) => setCharacterName(e.target.value)}
          />
        </div>
        <Button type="submit" size="sm" disabled={!actorId || addCast.isPending} className="gap-1.5 self-start">
          <Icon icon="ph:plus-bold" />
          Add to Cast
        </Button>
      </form>
      {availableActors.length === 0 && actors.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground/70">All known actors are already credited on this title.</p>
      )}
      {actors.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground/70">
          No actors in the directory yet — add some on the{' '}
          <Link href="/admin/actors" className="text-primary hover:underline">Actors</Link> page first.
        </p>
      )}
    </div>
  );
}

export default function AdminCatalogPage() {
  usePageTitle('Catalog · Kast');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [castSheetOpen, setCastSheetOpen] = useState(false);
  const [castMovie, setCastMovie] = useState(null);
  const [editingMovieId, setEditingMovieId] = useState(null);
  const { data, isLoading } = useAdminCatalog(page, 25, search);
  const setContentType = useSetMovieContentType();
  const setComingSoon = useSetMovieComingSoon();
  const setKidsContent = useSetMovieKidsContent();
  const { confirm } = useAlert();

  const movies = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  const handleContentTypeChange = async (movie, contentType) => {
    if (contentType === movie.ContentType) return;
    const ok = await confirm(
      `Change "${movie.Title}" from ${movie.ContentType} to ${contentType}?`,
      { title: 'Change Content Type', variant: 'warning', isConfirm: true, confirmLabel: `Set to ${contentType}` }
    );
    if (!ok) return;
    try {
      await setContentType.mutateAsync({ movieId: movie.Id, contentType });
      toast.success(`Set to ${contentType}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update.'));
    }
  };

  const handleComingSoonToggle = async (movie) => {
    const next = !movie.IsComingSoon;
    const ok = await confirm(
      next
        ? `Mark "${movie.Title}" as Coming Soon? It will be hidden from the live catalogue.`
        : `Move "${movie.Title}" to the live catalogue?`,
      { title: 'Coming Soon', isConfirm: true, confirmLabel: next ? 'Mark Coming Soon' : 'Move to Live' }
    );
    if (!ok) return;
    try {
      await setComingSoon.mutateAsync({ movieId: movie.Id, isComingSoon: next, releaseDate: null });
      toast.success(next ? 'Marked as Coming Soon.' : 'Moved to live catalogue.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update.'));
    }
  };

  const handleKidsContentToggle = async (movie) => {
    const next = !movie.IsKidsContent;
    const ok = await confirm(
      next
        ? `Mark "${movie.Title}" as kids-friendly? It will appear on Kids profiles.`
        : `Unmark "${movie.Title}" as kids-friendly?`,
      { title: 'Kids-Friendly', isConfirm: true, confirmLabel: next ? 'Mark Kids-Friendly' : 'Unmark Kids' }
    );
    if (!ok) return;
    try {
      await setKidsContent.mutateAsync({ movieId: movie.Id, isKidsContent: next });
      toast.success(next ? 'Marked as kids-friendly.' : 'Unmarked as kids-friendly.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update.'));
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:squares-four-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Catalog</h1>
            <p className="text-xs text-muted-foreground">Classify titles, mark Coming Soon, tag Kids content, and manage cast credits</p>
          </div>
        </div>
        {total > 0 && (
          <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
            {total} title{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="relative mb-5 max-w-sm">
        <Icon icon="ph:magnifying-glass-bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
        <input
          className={`${inputClass} pl-9`}
          placeholder="Search titles…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : movies.length === 0 ? (
        <EmptyState icon="ph:film-strip-bold">No titles found</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {movies.map((movie) => (
            <div
              key={movie.Id}
              className="overflow-hidden rounded-xl bg-white/[0.03] ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.04]"
            >
              <div className="flex flex-col gap-4 p-4 sm:flex-row">
                {movie.ThumbnailUrl ? (
                  <img
                    src={movie.ThumbnailUrl}
                    alt={movie.Title}
                    className="h-[170px] w-full shrink-0 rounded-lg object-cover sm:h-[110px] sm:w-[190px]"
                  />
                ) : (
                  <div className="flex h-[170px] w-full shrink-0 items-center justify-center rounded-lg bg-white/5 text-muted-foreground/40 sm:h-[110px] sm:w-[190px]">
                    <Icon icon="ph:image-bold" className="size-8" />
                  </div>
                )}

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{movie.Title}</h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center rounded-full bg-white/5 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-white/10">
                        {movie.CategoryName || 'Uncategorized'}
                      </span>
                      <StatusBadge status={movie.ApprovalStatus} />
                      {movie.IsComingSoon && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-primary/25">
                          <Icon icon="ph:clock-bold" className="size-3" />
                          Coming Soon
                        </span>
                      )}
                      {movie.IsKidsContent && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-400 ring-1 ring-emerald-500/25">
                          <Icon icon="ph:baby-bold" className="size-3" />
                          Kids
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    <Select
                      value={movie.ContentType}
                      onValueChange={(value) => handleContentTypeChange(movie, value)}
                      disabled={setContentType.isPending}
                    >
                      <SelectTrigger className="h-7 w-[110px] px-2 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Movie">Movie</SelectItem>
                        <SelectItem value="Series">Series</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      size="sm"
                      variant={movie.IsComingSoon ? 'default' : 'secondary'}
                      onClick={() => handleComingSoonToggle(movie)}
                      disabled={setComingSoon.isPending}
                      className="gap-1.5"
                    >
                      <Icon icon="ph:clock-bold" />
                      {movie.IsComingSoon ? 'Move to Live' : 'Mark Coming Soon'}
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => handleKidsContentToggle(movie)}
                      disabled={setKidsContent.isPending}
                      title="Controls whether this title appears on a Kids profile"
                      className={
                        movie.IsKidsContent
                          ? 'gap-1.5 bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 hover:bg-emerald-500/25'
                          : 'gap-1.5 bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      }
                    >
                      <Icon icon="ph:baby-bold" />
                      {movie.IsKidsContent ? 'Unmark Kids' : 'Mark Kids-Friendly'}
                    </Button>

                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => { setCastMovie(movie); setCastSheetOpen(true); }}
                      className="gap-1.5"
                    >
                      <Icon icon="ph:users-three-bold" />
                      Cast
                    </Button>

                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingMovieId(movie.Id)}
                      className="gap-1.5"
                    >
                      <Icon icon="ph:pencil-simple-bold" />
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={castSheetOpen} onOpenChange={setCastSheetOpen}>
        <SheetContent className="gap-0 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Cast</SheetTitle>
            <SheetDescription>{castMovie?.Title}</SheetDescription>
          </SheetHeader>
          {castMovie && <CastManager movieId={castMovie.Id} />}
        </SheetContent>
      </Sheet>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {editingMovieId && (
        <EditMovieModal movieId={editingMovieId} onClose={() => setEditingMovieId(null)} />
      )}
    </div>
  );
}
